import * as React from "react";
import { DayPicker, type Matcher } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const now = new Date();
const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

function Calendar({
  className,
  classNames,
  showOutsideDays = false,
  disabled: disabledProp,
  month: controlledMonth,
  defaultMonth,
  onMonthChange,
  ...props
}: CalendarProps) {
  const [internalMonth, setInternalMonth] = React.useState<Date>(
    defaultMonth ?? firstDayOfMonth,
  );

  const disabled = React.useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [{ before: today }];

    if (Array.isArray(disabledProp)) {
      matchers.push(...disabledProp);
    } else if (disabledProp) {
      matchers.push(disabledProp);
    }

    return matchers;
  }, [disabledProp]);

  const handleMonthChange = (nextMonth: Date) => {
    if (!controlledMonth) {
      setInternalMonth(nextMonth);
    }

    onMonthChange?.(nextMonth);
  };

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks={false}
      month={controlledMonth ?? internalMonth}
      onMonthChange={handleMonthChange}
      disabled={disabled}
      className={cn("relative p-2 sm:p-3", className)}
      classNames={{
        root: "relative",
        months: "relative flex w-full flex-col gap-6 md:flex-row md:gap-8",
        month: "w-full min-w-0 space-y-3 md:w-[320px]",
        month_caption: "relative flex h-10 items-center justify-center px-11",
        caption_label: "text-sm font-bold capitalize text-slate-900",
        nav: "pointer-events-none absolute inset-x-0 top-2 z-10 flex items-center justify-between sm:top-3",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "pointer-events-auto h-9 w-9 rounded-full bg-white p-0 opacity-100 shadow-sm hover:bg-slate-50",
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "pointer-events-auto h-9 w-9 rounded-full bg-white p-0 opacity-100 shadow-sm hover:bg-slate-50",
        ),
        chevron: "h-4 w-4",
        month_grid: "w-full table-fixed border-collapse",
        weekdays: "border-0",
        weekday:
          "h-9 p-0 text-center align-middle text-[0.72rem] font-semibold uppercase text-slate-400",
        weeks: "border-0",
        week: "border-0",
        day: "relative h-10 p-0 text-center align-middle text-sm",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "mx-auto flex aspect-square h-auto w-full max-w-10 items-center justify-center rounded-full p-0 font-medium text-slate-800 aria-selected:opacity-100",
        ),
        range_start:
          "rounded-l-full bg-primary/10 [&>button]:bg-primary [&>button]:text-primary-foreground",
        range_middle:
          "rounded-none bg-primary/10 [&>button]:rounded-none [&>button]:bg-transparent [&>button]:text-slate-900",
        range_end:
          "rounded-r-full bg-primary/10 [&>button]:bg-primary [&>button]:text-primary-foreground",
        selected:
          "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground",
        today:
          "[&>button]:font-black [&>button]:text-primary [&>button]:ring-1 [&>button]:ring-primary/30",
        outside: "text-slate-400 opacity-40",
        disabled:
          "cursor-not-allowed text-slate-300 opacity-40 [&>button]:cursor-not-allowed [&>button]:hover:bg-transparent",
        hidden: "invisible",
        ...classNames,
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
